import { useState, useEffect } from 'react'
import styled from '@emotion/styled'
import { Theme } from '../../styles/settings/theme'
import { Flex, Content } from '../atoms/Basics'
import ChartTitle from '../atoms/ChartTitle'
import ChartContainer from '../atoms/ChartContainer'
import Subtitle from '../atoms/Subtitle'
import TopicChart from '../molecules/TopicChart'
import Note from '../atoms/Note'
import YearSelector from '../atoms/yearSelector'
import { csv } from 'd3-fetch'

const Segment = styled.div`
  background-color: ${(props) => props.theme.colors.black};
  height: 0.5rem;
  border-radius: 50px;
`
const StyledFlex = styled(Flex)`
  :last-child {
    margin-left: 1rem;
  }
`
const TopicContent = styled(Content)`
  flex-wrap: wrap;
`

const Demographics = ({ note }) => {
  const $ = window.WILDLABS_VARS
  const [gender, setGender] = useState(null)
  const [allGenderData, setAllGenderData] = useState(null)
  const [availableYears, setAvailableYears] = useState([])
  const [selectedYear, setSelectedYear] = useState('All Years')

  useEffect(() => {
    // Load all demographic CSV files to extract years
    Promise.all([
      csv(process.env.PUBLIC_URL + '/data/demographicsGender.csv'),
      csv(process.env.PUBLIC_URL + '/data/demographicsRegion.csv'),
      csv(process.env.PUBLIC_URL + '/data/demographicsOrg.csv'),
      csv(process.env.PUBLIC_URL + '/data/demographicsRole.csv')
    ]).then(([genderData, regionData, orgData, roleData]) => {
      setAllGenderData(genderData)

      // Extract years from all CSV files
      const allData = [...genderData, ...regionData, ...orgData, ...roleData]
      const hasYearColumn =
        allData.length > 0 && allData[0].hasOwnProperty('year')

      if (hasYearColumn) {
        // Extract unique years from all data
        const years = [...new Set(allData.map((row) => row.year))].sort(
          (a, b) => {
            if (a === 'All Years') return -1
            if (b === 'All Years') return 1
            return a.localeCompare(b)
          }
        )
        setAvailableYears(years)

        // Set initial filtered data for default year
        const filteredData = genderData.filter(
          (row) => row.year === 'All Years'
        )
        setGender(filteredData)
      } else {
        // If no year column, use all data and set a default year
        setAvailableYears(['All Years'])
        setGender(genderData)
      }
    })
  }, [])

  useEffect(() => {
    if (allGenderData) {
      // Check if year column exists
      const hasYearColumn =
        allGenderData.length > 0 && allGenderData[0].hasOwnProperty('year')

      if (hasYearColumn) {
        const filteredData = allGenderData.filter(
          (row) => row.year === selectedYear
        )
        setGender(filteredData)
      } else {
        // If no year column, just use all data
        setGender(allGenderData)
      }
    }
  }, [selectedYear, allGenderData])

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value)
  }

  const topics = ['region', 'org', 'role']
  return (
    <ChartContainer>
      <ChartTitle text={$.demog_titles.chartTitle}>
        <YearSelector value={selectedYear} onChange={handleYearChange}>
          {availableYears.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </YearSelector>
      </ChartTitle>
      <Subtitle
        text={$.demog_titles.subtitle}
        size={Theme.fontSizes[4]}
        color={Theme.colors.gray}
      ></Subtitle>

      <Flex>
        {gender?.map((row, index) => (
          <StyledFlex
            key={
              row.year ? `${row.year}-${row.group}` : `${index}-${row.group}`
            }
            flexDirection="column"
            width={`${Math.round(row.percent * 100)}%`}
          >
            <div>
              <span>
                <b>{Math.round(row.percent * 100)}%</b>
              </span>
              &nbsp;{row.group}
            </div>
            <Segment />
          </StyledFlex>
        ))}
      </Flex>
      <TopicContent justifyContent="space-between">
        {topics.map((topic) => (
          <TopicChart
            key={topic}
            topic={topic}
            selectedYear={selectedYear}
          ></TopicChart>
        ))}
      </TopicContent>
      {note && <Note note={note} />}
    </ChartContainer>
  )
}

export default Demographics
